import React, {
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';

import {
  IconButton,
  Disclosure,
} from 'anf-core-react';
import {
  formatDayOfWeek,
  formatHours,
  getDropOffLocationImage,
} from '../../utils/drop-off-location-helper';

import TmntText from '../tmnt-text/tmnt-text';

const dropOffLocationProp = PropTypes.shape({
  address: PropTypes.shape({
    city: PropTypes.string.isRequired,
    postalCode: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    street: PropTypes.string.isRequired,
  }).isRequired,
  distance: PropTypes.number.isRequired,
  geoPosition: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
  locationId: PropTypes.string,
  name: PropTypes.string.isRequired,
  operatingHours: PropTypes.arrayOf(
    PropTypes.shape({
      dayOfWeek: PropTypes.string,
      closeHours: PropTypes.string,
      openHours: PropTypes.string,
    }),
  ),
  providerType: PropTypes.string.isRequired,
});

export const cardTypes = {
  list: 'list',
  infoWindow: 'info-window',
};

const propTypes = {
  cardType: PropTypes.string.isRequired,
  dropOffLocation: dropOffLocationProp.isRequired,
  onInfoWindowClose: PropTypes.func,
};

export default function DropOffLocationCard({
  cardType,
  dropOffLocation,
  onInfoWindowClose,
}) {
  const [isDisclosureExpanded, setIsDisclosureExpanded] = useState(false);
  const isInfoWindow = cardType === cardTypes.infoWindow;
  const storeHoursText = TmntText({ k: 'STOREHOURS', textOnly: true });

  const toggleDisclosure = useCallback(() => {
    if (isDisclosureExpanded) {
      setIsDisclosureExpanded(false);
    } else {
      setIsDisclosureExpanded(true);
    }
  }, [isDisclosureExpanded]);

  return (
    <div
      className={`drop-off-location-${cardType}-card`}
      data-testid={`drop-off-location-${cardType}-card`}
    >

      {isInfoWindow && (
        <div className="info-window-close-icon" data-testid="info-window-close-icon">
          <IconButton
            icon="close-thin"
            labelText="close"
            theme="inverse"
            variant="square"
            onClick={onInfoWindowClose}
          />
        </div>
      )}

      <div className={`${cardType}-card-logo-wrapper`}>
        <img className="drop-off-location-svg" src={getDropOffLocationImage(dropOffLocation)} alt={dropOffLocation.providerType} />
      </div>

      <div className={`${cardType}-card-info-wrapper`}>
        <p className={`${cardType}-drop-off-location-name`}>{dropOffLocation.name}</p>
        <p className={`${cardType}-info-row`}>{dropOffLocation.address.street}</p>
        <p className={`${cardType}-info-row`}>{`${dropOffLocation.address.city}, ${dropOffLocation.address.state}`}</p>
        <p className={`${cardType}-info-row distance-wrapper`}>{`${dropOffLocation.distance} miles`}</p>
        {(dropOffLocation?.operatingHours && dropOffLocation?.operatingHours[0].openHours) && (
          <div className={`${cardType}-info-row disclosure-wrapper`}>
            <Disclosure
              id={`${dropOffLocation.id}-store-hours-disclosure`}
              label={storeHoursText}
              onClick={toggleDisclosure}
              isExpanded={isDisclosureExpanded}
            >
              <table className="store-hours" role="presentation">
                <tbody>
                  {dropOffLocation.operatingHours.map((hoursObj, i) => {
                    const key = `${dropOffLocation.name}-hours-${i}`;
                    if (!hoursObj.openHours || !hoursObj.closeHours) return null;
                    return (
                      <tr key={key}>
                        <td className="day-of-week">{`${formatDayOfWeek(hoursObj.dayOfWeek)} `}</td>
                        <td>{`${formatHours(hoursObj.openHours)} - ${formatHours(hoursObj.closeHours)}`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Disclosure>
          </div>
        )}
      </div>
    </div>
  );
}

DropOffLocationCard.propTypes = propTypes;
